import { httpService } from '../services/httpService';
import { eventBus } from '../eventBus';

import type { LaudoPublico_GenerateLinkResponseDados } from '../contracts/LaudoPublico_GenerateLinkResponse';
import type { LaudoPublico_GetPublicResponseDados } from '../contracts/LaudoPublico_GetPublicResponse';

type GenerateLinkResponse = LaudoPublico_GenerateLinkResponseDados;
type PublicLaudoResponse = LaudoPublico_GetPublicResponseDados;

export type LaudoTipo = 'entrada' | 'saida';

// ── API calls ──────────────────────────────────────────────────────

export function useLaudoPublicLink() {
  /**
   * Generate (or reuse) the PERSISTENT public laudo link for an order item.
   * Staff-authenticated. Returns the token + ready-to-open public URL.
   */
  async function generateLaudoLink(orderItemId: number, tipo: LaudoTipo): Promise<GenerateLinkResponse | null> {
    const res = await httpService.post<{ dados: GenerateLinkResponse }>(
      '/index.php/laudo_links/generate_link',
      { order_item_id: orderItemId, tipo },
      { inputContract: 'LaudoPublico_GenerateLinkInput', responseContract: 'LaudoPublico_GenerateLinkResponse' },
    );
    return res?.dados ?? null;
  }

  /**
   * Fetch the PUBLIC read-only laudo view by token. No authentication —
   * the unguessable token IS the authorization.
   */
  async function fetchPublicLaudo(token: string): Promise<PublicLaudoResponse | null> {
    const res = await httpService.get<{ dados: PublicLaudoResponse }>(
      `/index.php/laudo_links/publico/${encodeURIComponent(token)}`,
      { responseContract: 'LaudoPublico_GetPublicResponse' },
    );
    return res?.dados ?? null;
  }

  /**
   * Gera o link do laudo e abre o modal de envio por WhatsApp. O link é
   * persistente e sempre enviável (sem gate de novo cliente). Retorna true se
   * abriu o modal; false se não havia id, o link não veio, ou houve erro de rede.
   * Engole erros — nunca bloqueia a confirmação de um laudo.
   */
  async function offerLaudoLink(orderItemId: number, tipo: LaudoTipo, fallbackNome = ''): Promise<boolean> {
    if (!orderItemId) return false;
    try {
      const dados = await generateLaudoLink(Number(orderItemId), tipo);
      if (!dados?.link_url) return false;
      eventBus.emit('modal.pdv-enviar-whatsapp.open', {
        tipo: tipo === 'entrada' ? 'laudo_entrada' : 'laudo_saida',
        clienteId: 0,
        clienteNome: dados.cliente_nome || fallbackNome,
        clienteTelefone: dados.cliente_telefone || '',
        linkLaudo: dados.link_url,
      });
      return true;
    } catch (_e) {
      return false;
    }
  }

  return { generateLaudoLink, fetchPublicLaudo, offerLaudoLink };
}
